import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import TableToolbar from '../ui/TableToolbar';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import UserAvatar from '../ui/UserAvatar';
import CopyablePhone from '../ui/CopyablePhone';
import SegmentedControl from '../ui/SegmentedControl';

function userSortValue(entry, key) {
  switch (key) {
    case 'username':
      return entry.username || '';
    case 'company_name':
      return entry.company_name || '';
    case 'created_at':
      return new Date(entry.created_at).getTime();
    case 'referrals_count':
      return entry.referrals_count || 0;
    case 'reward_points':
      return entry.reward_points || 0;
    default:
      return entry.username || '';
  }
}

function PlatformUsersView({
  users = [],
  companies = [],
  mode = 'all',
  companyFilter = 'all',
  onCompanyFilterChange,
  onViewUser,
}) {
  const { t } = useLanguage();
  const pa = t.platformAdmin;
  const adminT = t.admin;
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState(mode === 'clients' ? 'client' : 'all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const scopedUsers = useMemo(() => {
    let list = users;
    if (mode === 'clients') list = list.filter((entry) => entry.is_client);
    if (mode === 'vendors') list = list.filter((entry) => entry.is_vendor);
    if (companyFilter !== 'all') {
      list = list.filter(
        (entry) => entry.company_id != null && String(entry.company_id) === companyFilter,
      );
    }
    return list;
  }, [users, mode, companyFilter]);

  const typeCounts = useMemo(() => ({
    all: scopedUsers.length,
    admin: scopedUsers.filter((entry) => entry.is_admin).length,
    vendor: scopedUsers.filter((entry) => entry.is_vendor).length,
    client: scopedUsers.filter((entry) => entry.is_client).length,
  }), [scopedUsers]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = scopedUsers.filter((entry) => {
      if (typeFilter !== 'all') {
        if (typeFilter === 'admin' && !entry.is_admin) return false;
        if (typeFilter === 'vendor' && !entry.is_vendor) return false;
        if (typeFilter === 'client' && !entry.is_client) return false;
      }
      if (!term) return true;
      return [
        entry.username,
        entry.email,
        entry.company_name,
        ...(entry.phone_numbers || []),
        entry.referred_by_username,
        ...(entry.vendor_usernames || []),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });

    return [...filtered].sort((a, b) => {
      const aVal = userSortValue(a, sortBy);
      const bVal = userSortValue(b, sortBy);
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [scopedUsers, searchTerm, typeFilter, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDir(key === 'username' || key === 'company_name' ? 'asc' : 'desc');
  };

  const sortIndicator = (key) => (sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const showClientColumns = mode === 'clients' || typeFilter === 'client';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { label: adminT.totalUsers, value: scopedUsers.length, icon: UsersIcon, color: 'text-secondary-900 ' },
          { label: adminT.administrators, value: typeCounts.admin, icon: ShieldCheckIcon, color: 'text-amber-600' },
          { label: adminT.totalVendors, value: typeCounts.vendor, icon: UserGroupIcon, color: 'text-red-600' },
          { label: adminT.totalClients, value: typeCounts.client, icon: UserIcon, color: 'text-success-600' },
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04 }}
              className="card-premium p-4"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-secondary-500 ">{stat.label}</p>
                  <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
                </div>
                <Icon className="h-8 w-8 text-orange-500 opacity-40" />
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="card-premium overflow-hidden">
        <div className="border-b border-secondary-200/70 p-4 ">
          {mode === 'all' ? (
            <div className="mb-4 max-lg:mobile-scroll-x">
              <SegmentedControl
                value={typeFilter}
                onChange={setTypeFilter}
                options={[
                  { value: 'all', label: `${adminT.all} (${typeCounts.all})` },
                  { value: 'admin', label: `${adminT.admins || 'Admins'} (${typeCounts.admin})` },
                  { value: 'vendor', label: `${adminT.vendors} (${typeCounts.vendor})` },
                  { value: 'client', label: `${adminT.clients} (${typeCounts.client})` },
                ]}
                layoutId="platform-user-type-filter"
                aria-label={pa.usersTitle}
                className="max-lg:shrink-0"
              />
            </div>
          ) : null}

          <TableToolbar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder={pa.searchUsers}
            filters={[
              {
                id: 'company',
                label: pa.company,
                value: companyFilter,
                onChange: onCompanyFilterChange,
                options: [
                  { value: 'all', label: pa.allCompanies },
                  ...companies.map((company) => ({
                    value: String(company.id),
                    label: company.name,
                  })),
                ],
              },
              {
                id: 'sort',
                label: pa.sortBy,
                value: sortBy,
                onChange: setSortBy,
                options: [
                  { value: 'created_at', label: adminT.dateJoined },
                  { value: 'username', label: adminT.username },
                  { value: 'company_name', label: pa.company },
                  { value: 'referrals_count', label: adminT.totalReferrals },
                  { value: 'reward_points', label: pa.rewardPoints },
                ],
              },
            ]}
            hasActiveFilters={searchTerm.trim() !== '' || companyFilter !== 'all' || typeFilter !== 'all'}
            onClearFilters={() => {
              setSearchTerm('');
              onCompanyFilterChange?.('all');
              setTypeFilter(mode === 'clients' ? 'client' : 'all');
            }}
            resultCount={filteredUsers.length}
            totalCount={scopedUsers.length}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200 ">
            <thead className="bg-secondary-50 ">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                  <button type="button" onClick={() => toggleSort('username')} className="hover:text-orange-600">{adminT.user}{sortIndicator('username')}</button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                  <button type="button" onClick={() => toggleSort('company_name')} className="hover:text-orange-600">{pa.company}{sortIndicator('company_name')}</button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">{adminT.type}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">{adminT.contact}</th>
                {showClientColumns ? (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">{pa.clientState}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                      <button type="button" onClick={() => toggleSort('reward_points')} className="hover:text-orange-600">{pa.rewardPoints}{sortIndicator('reward_points')}</button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                      <button type="button" onClick={() => toggleSort('referrals_count')} className="hover:text-orange-600">{adminT.totalReferrals}{sortIndicator('referrals_count')}</button>
                    </th>
                  </>
                ) : null}
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                  <button type="button" onClick={() => toggleSort('created_at')} className="hover:text-orange-600">{adminT.joined}{sortIndicator('created_at')}</button>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-secondary-500">{adminT.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200 bg-white  ">
              {filteredUsers.map((entry) => (
                <tr key={`${entry.user_type}-${entry.id}`} className="hover:bg-secondary-50/70 ">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <UserAvatar src={entry.avatar_url} alt={entry.username} size="sm" />
                      <div>
                        <p className="font-medium text-secondary-900 ">{entry.username}</p>
                        <p className="text-xs text-secondary-500 ">{entry.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-secondary-700 ">
                    {entry.company_name || pa.platformLevel}
                  </td>
                  <td className="px-4 py-4">
                    <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-700  ">
                      {entry.is_admin ? adminT.administrators : entry.is_vendor ? adminT.vendors : adminT.clients}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-secondary-600 ">
                    <CopyablePhone value={entry.phone_numbers} fallback={adminT.noPhone} />
                  </td>
                  {showClientColumns ? (
                    <>
                      <td className="px-4 py-4 text-sm capitalize text-secondary-700 ">
                        {entry.is_client ? (entry.state || '—') : '—'}
                      </td>
                      <td className="px-4 py-4 text-sm text-secondary-700 ">
                        {entry.is_client ? entry.reward_points ?? 0 : '—'}
                      </td>
                      <td className="px-4 py-4 text-sm text-secondary-700 ">
                        {entry.is_client ? entry.referrals_count ?? 0 : entry.clients_count ?? '—'}
                      </td>
                    </>
                  ) : null}
                  <td className="px-4 py-4 text-sm text-secondary-600 ">
                    {new Date(entry.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <Button type="button" variant="secondary" size="compact" onClick={() => onViewUser?.(entry)}>
                      {adminT.viewDetails}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 ? (
            <EmptyState compact icon={UsersIcon} title={adminT.noUsersFound} description={adminT.tryAdjustingFilters} className="py-10" />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default PlatformUsersView;
